import { useCallback, useState } from "react";
import { useAccountant } from "./AccountantContext";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import {
    deleteProfile,
    getBusinessesAndAccounts,
    getOauthUrl,
    getProfileList,
    refreshAgencyOverdue,
    updateAccountMapping,
} from "@/service/MyobService";

export default function MyobConnectionTab() {
    const [state, dispatch] = useAccountant();
    const [buttonLoading, setButtonLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    const handleConnect = useCallback(() => {
        getOauthUrl()
            .then(res => { if (!res.ok) throw new Error('Failed to get OAuth URL'); return res.json(); })
            .then(data => { window.location.href = data.message; })
            .catch(err => { dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } }); });
    }, [dispatch]);

    const handleRefresh = useCallback(() => {
        setButtonLoading(true);
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        getProfileList()
            .then(res => { if (!res.ok) throw new Error('Failed to load profiles'); return res.json(); })
            .then(profiles => {
                dispatch({ type: 'SET_MYOB_PROFILES', payload: profiles });
                return getBusinessesAndAccounts('true');
            })
            .then(res => { if (!res.ok) throw new Error('Failed to load businesses'); return res.json(); })
            .then(data => {
                dispatch({ type: 'SET_BUSINESSES_ACCOUNTS', payload: data });
                dispatch({ type: 'SET_LOADING', payload: false });
                setButtonLoading(false);
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                setButtonLoading(false);
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch]);

    const handleAccountChange = useCallback((profileId, businessId, prop, value) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        updateAccountMapping(profileId, businessId, prop, value)
            .then(() => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: 'Account mapping updated', isSuccess: true } });
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch]);

    const handleOverdueSync = useCallback(() => {
        setSyncLoading(true);
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        refreshAgencyOverdue()
            .then(async res => {
                if (!res.ok) {
                    const body = await res.json().catch(() => null);
                    if (res.status === 409) {
                        throw new Error('MYOB overdue synchronization is already running.');
                    }
                    throw new Error(body?.message || 'Failed to synchronize MYOB overdue invoices');
                }
                return res.json();
            })
            .then(result => {
                const failed = (result.businesses_failed || 0)
                    + (result.agency_block_failures || 0)
                    + (result.agency_unblock_failures || 0);
                const message = `MYOB overdue sync completed: ${result.agencies_checked || 0} checked, `
                    + `${result.agencies_blocked || 0} blocked, ${result.agencies_unblocked || 0} unblocked, `
                    + `${failed} failed.`;
                const severity = failed > 0 || result.holiday_calendar_fallback ? 'warning' : 'success';
                dispatch({
                    type: 'SET_NOTIFICATION',
                    payload: {
                        message: result.holiday_calendar_fallback
                            ? `${message} Google Calendar fallback was used.`
                            : message,
                        isSuccess: severity === 'success',
                        severity,
                    },
                });
            })
            .catch(err => {
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            })
            .finally(() => setSyncLoading(false));
    }, [dispatch]);

    const handleDelete = useCallback((profileUid) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        deleteProfile(profileUid)
            .then(res => {
                if (!res.ok) return res.json().then(e => { throw new Error(e.message); });
                dispatch({ type: 'DELETE_PROFILE', payload: profileUid });
                dispatch({ type: 'SET_LOADING', payload: false });
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch]);

    return (
        <div>
            <div className="backoffice-toolbar px-3">
                <Button variant="contained" color="primary" onClick={handleConnect} sx={{ textTransform: 'none' }}>
                    Connect to MyOB
                </Button>
                <Button variant="contained" color="primary" href="https://secure.myob.com/" target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>
                    Revoke Access
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRefresh}
                    disabled={buttonLoading}
                    startIcon={buttonLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: 'none' }}
                >
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleOverdueSync}
                    disabled={syncLoading}
                    startIcon={syncLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: 'none' }}
                >
                    Sync Overdue Invoices
                </Button>
            </div>

            {state.notification && (
                <Alert severity={state.notification.severity || (state.notification.isSuccess ? 'success' : 'error')} sx={{ mb: 2 }}>
                    {state.notification.message}
                </Alert>
            )}

            {state.myobProfiles.map(profile => (
                <Accordion key={profile.uid}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ mr: 4 }}>{profile.username}</Typography>
                        <Typography sx={{ color: 'text.secondary', flexGrow: 1 }}>
                            Expires after: {profile.refresh_token_expires_date}
                        </Typography>
                        <IconButton
                            color="error"
                            aria-label={`Delete profile ${profile.username}`}
                            onClick={(e) => { e.stopPropagation(); handleDelete(profile.uid); }}
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </AccordionSummary>
                    <AccordionDetails>
                        {(state.businessesAndAccounts[profile.uid] || []).map(business => (
                            <Card key={business.uid} sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="h6">{business.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Business UID: {business.uid}
                                    </Typography>

                                    <Typography variant="subtitle2" align="center" sx={{ mt: 2, mb: 1 }}>
                                        Accounts Setup:
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {state.accountFullList.map(accountTemplate => (
                                            <Grid item xs={12} sm={6} md={2.4} key={accountTemplate.prop}>
                                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                    {accountTemplate.name}
                                                </Typography>
                                                <FormControl variant="standard" fullWidth size="small">
                                                    <Select
                                                        value={state.accountsSelection?.[profile.uid]?.[business.uid]?.[accountTemplate.prop] || ''}
                                                        onChange={(e) => handleAccountChange(profile.uid, business.uid, accountTemplate.prop, e.target.value)}
                                                        displayEmpty
                                                    >
                                                        <MenuItem value="" disabled>MyOB Account</MenuItem>
                                                        {(business.accounts || []).map(account => (
                                                            <MenuItem key={account.uid} value={account.uid}>
                                                                {account.display_id}-{account.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="subtitle2" align="center" sx={{ mb: 1 }}>
                                        Contacts:
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {(business.contacts || []).map(contact => (
                                            <Grid item xs={2} key={contact.uid}>
                                                <Typography variant="body2">{contact.name}</Typography>
                                            </Grid>
                                        ))}
                                        {(!business.contacts || business.contacts.length === 0) && (
                                            <Grid item><Typography variant="body2" color="text.secondary">No contacts</Typography></Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                        {(!state.businessesAndAccounts[profile.uid] || state.businessesAndAccounts[profile.uid].length === 0) && (
                            <Typography color="text.secondary">No businesses found for this profile.</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}

            {state.myobProfiles.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
                    No MyOB profiles connected. Click "Connect to MyOB" to get started.
                </Typography>
            )}
        </div>
    );
}
